import React from "react";
import { size } from "lodash";
import styled from "styled-components";
import { formatDate, tz } from "@backpacker/date";
import DateFromNow from "@backpacker/date/from-now";
import {
  FontWeight,
  Radius,
  Spacing,
  border,
  content,
  ellipsis,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import Icon from "@ui-kit/icon";
import Portrait from "@ui-kit/identity/portrait";
import SafeHTML from "@ui-kit/safe-html";
import Tooltip from "@ui-kit/tooltip";
import Image from "@ui-kit/utils/image";
import { Quote } from "@radarly/models/posts";

export interface IProps {
  quote: Quote;
  timezone: Timezone;
  locale: Locale;
  className?: string;
}

const Root = styled.div`
  border-radius: ${Radius.Small};
  border: 1px solid ${border};
  padding: ${Spacing.Medium};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.Medium};
`;

const Header = styled.div`
  ${ellipsis()};
  display: flex;
  gap: ${Spacing.ExtraSmall};
  line-height: 24px;
  color: ${(props) => content(props, "secondary")};
  white-space: nowrap;

  a {
    color: inherit;
  }
`;

const NameAndVerified = styled.div`
  display: flex;
  gap: ${Spacing.ExtraSmall};
  align-items: center;
  font-weight: ${FontWeight.Bold};
  color: ${content};
`;

const Name = styled.div`
  ${ellipsis(200)};
`;

const Body = styled.div`
  display: flex;
  gap: ${Spacing.Medium};
`;

const ImageWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  overflow: hidden;
  width: 92px;
  height: 92px;
  background-color: ${(props) =>
    transparentize(0.5, content(props, "tertiary"))};
`;

const Component = ({ quote = {}, locale, timezone, className }: IProps) => {
  const { date, permalink, user = {}, media = {}, text } = quote;
  const hasImage = size(media?.image) > 0;
  const { profileImg, name, screenName, verified } = user;

  const fromNow = (
    <DateFromNow date={date} locale={locale} timezone={timezone} />
  );

  const handleClick = (e) => {
    e.stopPropagation();
    permalink && window.open(permalink);
  };

  return (
    <Root className={className} onClick={handleClick}>
      <Header>
        <Portrait type="account" picture={profileImg} size="s" />
        <NameAndVerified>
          <Name>{name}</Name>
          {!!verified && <Icon color="#3C8DB6" glyph="verified" />}
        </NameAndVerified>
        {screenName}
        <span>•</span>
        <Tooltip
          tooltip={formatDate(tz(date, timezone), locale, "LL - LT")}
          disabled={!permalink}
        >
          {permalink ? (
            <a href={permalink} target="_blank" rel="noopener noreferrer">
              {fromNow}
            </a>
          ) : (
            <span>{fromNow}</span>
          )}
        </Tooltip>
      </Header>
      <Body>
        {hasImage && (
          <ImageWrapper>
            <Image image={media.image[0]} lazy animated />
          </ImageWrapper>
        )}
        <SafeHTML html={text} />
      </Body>
    </Root>
  );
};

export default Component;
